import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coachAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';
import CoachLayout from './CoachLayout';
import AccessDenied from '../../components/AccessDenied';

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];
const CONCEPT_TYPES = ['general', 'quick_game', 'play_action', 'run', 'screen', 'deep_shot', 'option', 'blitz_pickup'];

function PlayModal({ domain, play, onSave, onClose }) {
  const [form, setForm] = useState({
    display_name: play?.display_name || '',
    description: play?.description || '',
    difficulty: play?.difficulty || 'beginner',
    concept_type: play?.concept_type || 'general',
    formation_id: play?.formation_id || '',
    coaching_notes: play?.coaching_notes || '',
    tags: (play?.tags || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.display_name.trim()) { setError('Play name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (play) {
        await coachAPI.updatePlay(domain, play.play_id, payload);
      } else {
        await coachAPI.createPlay(domain, payload);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 580, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 className="coach-modal-title">{play ? 'Edit Play' : 'New Play'}</h3>
        {error && <div className="coach-error">⚠ {error}</div>}
        <div className="coach-form-group">
          <label className="coach-label">Play Name *</label>
          <input className="coach-input" value={form.display_name} onChange={set('display_name')} placeholder="e.g. Four Verticals" />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Description</label>
          <textarea className="coach-textarea" value={form.description} onChange={set('description')} placeholder="What does this play do?" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="coach-form-group">
            <label className="coach-label">Difficulty</label>
            <select className="coach-select" value={form.difficulty} onChange={set('difficulty')}>
              {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
            </select>
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Concept Type</label>
            <select className="coach-select" value={form.concept_type} onChange={set('concept_type')}>
              {CONCEPT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Formation ID</label>
          <input className="coach-input" value={form.formation_id} onChange={set('formation_id')} placeholder="e.g. trips_right" />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Coaching Notes</label>
          <textarea className="coach-textarea" value={form.coaching_notes} onChange={set('coaching_notes')} placeholder="Key teaching points for this play..." />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Tags (comma-separated)</label>
          <input className="coach-input" value={form.tags} onChange={set('tags')} placeholder="motion, zone_read, short_passing" />
        </div>
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : play ? 'Update Play' : 'Create Play'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlayLibraryPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlay, setEditingPlay] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterDiff) params.difficulty = filterDiff;
    if (filterType) params.concept_type = filterType;
    coachAPI.listPlays(domain, params)
      .then(({ data }) => setPlays(data.plays || []))
      .catch(e => {
        if (e.response?.status === 403) { setAccessDenied(true); }
        else { setError(e.response?.data?.detail || e.message); }
      })
      .finally(() => setLoading(false));
  }, [domain, filterDiff, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (playId, name) => {
    const ok = await confirm({
      title: 'Delete play?',
      message: `Delete play "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await coachAPI.deletePlay(domain, playId).catch(() => {});
    toast.info('Play deleted');
    load();
  };

  const filtered = plays.filter(p =>
    !search || p.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (accessDenied) return <AccessDenied domainId={domain} onBack={() => navigate('/coach')} />;

  return (
    <CoachLayout title={`Play Library — ${domain.charAt(0).toUpperCase() + domain.slice(1)}`}>
      {error && <div className="coach-error">⚠ {error}</div>}

      {/* Filters */}
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">
            {plays.length} Plays
            <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: 8 }}>
              enrolled learners can access all plays below
            </span>
          </h2>
          <button className="btn btn-primary" onClick={() => { setEditingPlay(null); setShowModal(true); }}>
            + New Play
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input
            className="coach-input"
            style={{ flex: '1 1 200px' }}
            placeholder="Search plays…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="coach-select" style={{ flex: '0 0 140px' }} value={filterDiff} onChange={e => setFilterDiff(e.target.value)}>
            <option value="">All difficulties</option>
            {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className="coach-select" style={{ flex: '0 0 160px' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All concepts</option>
            {CONCEPT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="coach-loading">Loading play library…</div>
        ) : filtered.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">🏈</span>
            <div className="coach-empty-title">No plays yet</div>
            <div className="coach-empty-desc">Click "+ New Play" to add plays to the library.</div>
          </div>
        ) : (
          <table className="coach-table">
            <thead>
              <tr>
                <th>Play Name</th>
                <th>Concept</th>
                <th>Difficulty</th>
                <th>Formation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(play => (
                <tr key={play.play_id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{play.display_name}</div>
                    {play.description && <div className="text-muted text-sm">{play.description.slice(0, 80)}{play.description.length > 80 ? '…' : ''}</div>}
                  </td>
                  <td><span className="badge badge-draft">{(play.concept_type || '').replace(/_/g, ' ')}</span></td>
                  <td><span className={`badge badge-${play.difficulty === 'advanced' ? 'published' : play.difficulty === 'intermediate' ? 'draft' : 'draft'}`}>{play.difficulty}</span></td>
                  <td className="text-muted text-sm">{play.formation_id || '—'}</td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-outline btn-sm" onClick={() => { setEditingPlay(play); setShowModal(true); }}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(play.play_id, play.display_name)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <PlayModal
          domain={domain}
          play={editingPlay}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </CoachLayout>
  );
}
